import React, {useEffect, useState} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    Box,
    IconButton,
} from '@mui/material';
import instance from '../../../instance';
import CallNotification from '../../../context/NotificationContext';
import CloseIcon from '@mui/icons-material/Close';

interface EditMerchantPopupProps {
    open: boolean;
    userId: number | null;
    serviceCenterId: number | null;
    onClose: () => void;
    onSuccess: (updatedMerchant: any) => void;
}

const EditMerchantPopup = ({open, userId, serviceCenterId, onClose, onSuccess}: EditMerchantPopupProps) => {
    const {handleErrorNotification} = CallNotification();
    const [form, setForm] = useState({
        name: '',
        inn: '',
        address: '',
        e_mail: '',
        phone: '',
        flag_vat: false,
    });
    const [errors, setErrors] = useState<{[key: string]: string}>({});
    const [loading, setLoading] = useState(false);
    const [initialForm, setInitialForm] = useState<typeof form | null>(null);

    const fetchMerchant = async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const response = await instance.post('/LK', {
                method: 'GetMerchant',
                params: {user_id: userId},
            });
            handleErrorNotification(response);
            const data = response.data?.merchant;
            if (data) {
                const prepared = {
                    name: data.name || '',
                    inn: data.inn || '',
                    address: data.address || '',
                    e_mail: data.e_mail || '',
                    phone: data.phone || '',
                    flag_vat: data.flag_vat === 'Y',
                };
                setForm(prepared);
                setInitialForm(prepared);
            }
        } catch (error) {
            console.error('Ошибка при загрузке данных мерчанта', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: keyof typeof form, value: any) => {
        setForm(prev => ({...prev, [field]: value}));
    };

    const handleSave = async () => {
        const errs: typeof errors = {};
        if (!form.name) errs.name = 'Название обязательно';
        if (!form.address) errs.address = 'Адрес обязателен';
        if (!form.e_mail) errs.e_mail = 'Email обязателен';
        if (!form.phone) errs.phone = 'Телефон обязателен';

        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }

        setLoading(true);
        try {
            const response = await instance.post('/LK', {
                method: 'EditMerchant',
                params: {
                    user_id: userId,
                    ...form,
                    flag_vat: form.flag_vat ? 'Y' : 'N',
                    service_center_id: serviceCenterId,
                },
            });
            handleErrorNotification(response, true);
            if (response.data?.error === 0) {
                const updated = await instance.post('/LK', {
                    method: 'GetMerchant',
                    params: {user_id: userId},
                });
                handleErrorNotification(updated);
                onSuccess(updated.data?.merchant);
                onClose();
            }
        } catch (err) {
            console.error('Ошибка при сохранении мерчанта', err);
        } finally {
            setLoading(false);
        }
    };

    const isChanged = JSON.stringify(form) !== JSON.stringify(initialForm);

    useEffect(() => {
        if (open && userId) {
            void fetchMerchant();
        }
    }, [open, userId]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <div style={{padding: '20px 24px 10px 3px', position: 'relative', backgroundColor: '#f5f7ff'}}>
                <DialogTitle sx={{pr: 5}}>Редактирование мерчанта</DialogTitle>
                <IconButton onClick={onClose} style={{position: 'absolute', right: 16, top: 16}}>
                    <CloseIcon />
                </IconButton>
            </div>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" pt={1} sx={{minHeight: '370px'}}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" gap={2} pt={1} sx={{minHeight: '370px'}}>
                        <TextField
                            label="ИИН/БИН"
                            value={form.inn}
                            disabled={true}
                        />
                        <TextField
                            label="Название организации"
                            value={form.name}
                            onChange={e => handleChange('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                            required
                        />
                        <TextField
                            label="Email"
                            value={form.e_mail}
                            onChange={e => handleChange('e_mail', e.target.value)}
                            error={!!errors.e_mail}
                            helperText={errors.e_mail}
                            required
                        />
                        <TextField
                            label="Телефон"
                            value={form.phone}
                            onChange={e => handleChange('phone', e.target.value)}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            required
                        />
                        <TextField
                            label="Адрес"
                            value={form.address}
                            onChange={e => handleChange('address', e.target.value)}
                            error={!!errors.address}
                            helperText={errors.address}
                            required
                        />
                        <FormControlLabel
                            label="Организация является плательщиком НДС"
                            control={
                                <Checkbox
                                    checked={form.flag_vat}
                                    onChange={e => handleChange('flag_vat', e.target.checked)}
                                />
                            }
                        />
                    </Box>
                )}
                <Box sx={{mt: 2}}>
                    <Button type="submit" sx={{mr: 2}} disabled={!isChanged || loading}
                            onClick={() => void handleSave()}>
                        Сохранить
                    </Button>
                    <Button variant="text" onClick={onClose} disabled={loading}>
                        Отмена
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default EditMerchantPopup;
